import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Botao,
  Campo,
  CampoDeSelecao,
  CampoDeTexto,
  MenuDeAcoes,
  Modal,
  Selo,
  juntarClasses,
  useDialogo,
} from '../../componentes/ui';
import { useIdentidade } from '../../hooks/useSessao';
import {
  TIPOS_DE_ATIVIDADE,
  carregarParticipantesPossiveis,
  criarAtividade,
  editarAtividade,
  excluirAtividade,
  formatarDataIso,
  hojeIso,
  rotuloDoTipo,
  situacaoDoPrazo,
  type AtividadeDoCrm,
  type TipoDeAtividade,
} from '../../servicos/crm';
import { ErroDaApi } from '../ErroDaApi';
import { iniciais } from './CardDoPipeline';

/**
 * Uma atividade numa lista — no card e na tela de Atividades.
 *
 * A caixa conclui (e reabre) na hora. O prazo segue as cores do card: vermelho
 * vencido, laranja hoje. Na tela de Atividades a linha diz de que empresa é e
 * leva ao card; dentro do card, isso seria repetir o título da janela.
 */
export function LinhaDeAtividade(props: {
  readonly atividade: AtividadeDoCrm;
  readonly mostrarEmpresa?: boolean;
  readonly aoEditar: () => void;
}): JSX.Element {
  const a = props.atividade;
  const queryClient = useQueryClient();
  const dialogo = useDialogo();
  const concluida = a.concluidaEm !== null;
  const prazo = concluida ? null : situacaoDoPrazo(a.prazo);

  const recarregar = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['crm'] });
  };
  const alternar = useMutation({
    mutationFn: () => editarAtividade(a.id, { concluida: !concluida }),
    onSettled: recarregar,
  });
  const apagar = useMutation({ mutationFn: () => excluirAtividade(a.id), onSettled: recarregar });

  return (
    <li className="flex items-start gap-3 rounded-card border border-borda bg-superficie-elevada px-3 py-2.5">
      <input
        type="checkbox"
        checked={concluida}
        disabled={alternar.isPending}
        onChange={() => alternar.mutate()}
        aria-label={concluida ? `Reabrir: ${a.titulo}` : `Concluir: ${a.titulo}`}
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-borda bg-superficie-base text-acento-ciano"
      />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <Selo tom="neutro" tamanho="pequeno" semPonto>
            {rotuloDoTipo(a.tipo)}
          </Selo>
          <span
            className={juntarClasses(
              'text-sm text-conteudo-primario',
              concluida && 'text-conteudo-sutil line-through',
            )}
          >
            {a.titulo}
          </span>
        </div>
        {a.descricao !== null && a.descricao !== '' && (
          <p className="line-clamp-2 whitespace-pre-wrap text-xs text-conteudo-secundario">
            {a.descricao}
          </p>
        )}
        <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-conteudo-sutil">
          <span
            className={juntarClasses(
              prazo === 'vencida' && 'font-medium text-erro',
              prazo === 'hoje' && 'font-medium text-atencao',
            )}
          >
            {prazo === 'hoje' ? 'Hoje' : formatarDataIso(a.prazo)}
            {a.hora !== null && ` às ${a.hora}`}
            {prazo === 'vencida' && ' · atrasada'}
          </span>
          <span className="flex items-center gap-1.5" title={`Responsável: ${a.responsavel.nome}`}>
            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-acento-indigo/15 text-[0.55rem] font-semibold text-acento-indigo">
              {iniciais(a.responsavel.nome)}
            </span>
            {a.responsavel.nome}
          </span>
          {props.mostrarEmpresa === true && (
            <Link
              to={`/crm/pipeline?card=${a.card.id}`}
              className="text-conteudo-secundario underline-offset-2 hover:text-conteudo-primario hover:underline"
            >
              {a.card.nomeFantasia?.trim() || a.card.razaoSocial}
            </Link>
          )}
        </p>
        {(alternar.error ?? apagar.error) !== null && (
          <ErroDaApi titulo="Não foi possível salvar" erro={alternar.error ?? apagar.error} />
        )}
      </div>
      <MenuDeAcoes
        rotulo={a.titulo}
        itens={[
          { rotulo: 'Editar', aoEscolher: props.aoEditar },
          {
            rotulo: 'Excluir',
            perigo: true,
            aoEscolher: async () => {
              const ok = await dialogo.confirmar({
                titulo: `Excluir a atividade "${a.titulo}"?`,
                rotuloConfirmar: 'Excluir',
                tom: 'perigo',
              });
              if (ok) apagar.mutate();
            },
          },
        ]}
      />
    </li>
  );
}

/**
 * Criar ou editar uma atividade. O responsável começa em quem está criando —
 * o caso comum é anotar a própria tarefa — e pode ser qualquer admin.
 */
export function FormularioDeAtividade(props: {
  /** Presente: edita esta. Ausente: cria no `cardId`. */
  readonly atividade?: AtividadeDoCrm;
  readonly cardId?: string;
  readonly aoFechar: () => void;
}): JSX.Element {
  const a = props.atividade;
  const identidade = useIdentidade();
  const queryClient = useQueryClient();
  const [titulo, setTitulo] = useState(a?.titulo ?? '');
  const [tipo, setTipo] = useState<TipoDeAtividade>(a?.tipo ?? 'LIGACAO');
  const [prazo, setPrazo] = useState(a?.prazo ?? hojeIso());
  const [hora, setHora] = useState(a?.hora ?? '');
  const [responsavelId, setResponsavelId] = useState(
    a?.responsavel.accountId ?? identidade?.accountId ?? '',
  );
  const [descricao, setDescricao] = useState(a?.descricao ?? '');

  const pessoas = useQuery({
    queryKey: ['crm', 'participantes-possiveis'],
    queryFn: carregarParticipantesPossiveis,
  });

  const salvar = useMutation({
    mutationFn: () => {
      const dados = {
        titulo: titulo.trim(),
        tipo,
        prazo,
        hora: hora === '' ? null : hora,
        responsavelId,
        descricao: descricao.trim(),
      };
      return a === undefined
        ? criarAtividade(props.cardId ?? '', dados)
        : editarAtividade(a.id, dados);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['crm'] });
      props.aoFechar();
    },
  });

  const valido = titulo.trim() !== '' && prazo !== '' && responsavelId !== '';

  return (
    <Modal
      aberto
      aoFechar={props.aoFechar}
      titulo={a === undefined ? 'Nova atividade' : 'Editar atividade'}
      rodape={
        <>
          <Botao variante="secundario" onClick={props.aoFechar}>
            Cancelar
          </Botao>
          <Botao disabled={!valido} carregando={salvar.isPending} onClick={() => salvar.mutate()}>
            {a === undefined ? 'Criar atividade' : 'Salvar'}
          </Botao>
        </>
      }
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (valido) salvar.mutate();
        }}
      >
        <Campo
          rotulo="O que fazer"
          obrigatorio
          autoFocus
          maxLength={200}
          placeholder="Ex.: Ligar para o diretor financeiro, cobrar SPED"
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
        />
        <div className="grid gap-4 sm:grid-cols-3">
          <CampoDeSelecao
            rotulo="Tipo"
            value={tipo}
            onChange={(e) => setTipo(e.target.value as TipoDeAtividade)}
            opcoes={TIPOS_DE_ATIVIDADE.map((t) => ({ valor: t.tipo, rotulo: t.rotulo }))}
          />
          <Campo
            rotulo="Prazo"
            obrigatorio
            type="date"
            value={prazo}
            onChange={(e) => setPrazo(e.target.value)}
          />
          <Campo
            rotulo="Hora"
            type="time"
            ajuda="Opcional."
            value={hora}
            onChange={(e) => setHora(e.target.value)}
          />
        </div>
        <CampoDeSelecao
          rotulo="Responsável"
          value={responsavelId}
          onChange={(e) => setResponsavelId(e.target.value)}
          opcoes={(pessoas.data ?? []).map((p) => ({
            valor: p.accountId,
            rotulo: p.accountId === identidade?.accountId ? `${p.nome} (você)` : p.nome,
          }))}
        />
        <CampoDeTexto
          rotulo="Detalhes"
          ajuda="Opcional: com quem falar, o que levar, o que ficou combinado."
          rows={3}
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
        />
        {salvar.isError && (
          <ErroDaApi titulo="Não foi possível salvar a atividade" erro={salvar.error} />
        )}
        <button type="submit" className="sr-only">
          Salvar
        </button>
      </form>
    </Modal>
  );
}
